import { Component, OnInit, Input, AfterViewInit, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy, SimpleChanges, OnChanges } from '@angular/core';
import { AlertsService } from 'src/modules/base/services/alerts.service';
import { LoaderService } from 'src/modules/base/services/loader.service';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { RolePermissionService } from 'src/modules/base/shared/services/role-permission.service';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DialogConfig } from '../../../dialog/dialog-config';
import { AIService } from '../../services/ai.service';

declare var Chart: any;
declare var mermaid: any;

@Component({
    selector: 'kx-ai-data-analysis-output',
    templateUrl: './kx-ai-data-analysis-output.component.html',
    styleUrls: ['./kx-ai-data-analysis-output.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KxAiDataAnalysisOutputComponent implements OnInit, OnDestroy, OnChanges {
 
  @Input() set config(value: any) {
    if (value) {
      this._config = value;
      this.initializeComponent(value);
    }
  }
  get config(): any {
    return this._config;
  }
  private _config: any = { defaultViewMode: false, loaded: false, noData: false, outputType: "html", errMsg: "" };
  
  results: {
    insights?: string[];
    visualizations?: Array<{
      title: string;
      data: string; // base64 image data
    }>;
    tables?: Array<{
      title: string;
      markdown: string;
    }>;
    metadata?: {
      processed_rows: number;
      generated_at: string;
    };
  } = {};

  renderedContent: SafeHtml | string = '';
  isLoaded: boolean = false;

  constructor(
    public rolePermissionService: RolePermissionService,
    public baseService: BaseService,
    public alertService: AlertsService,
    public loader: LoaderService,
    public aiService: AIService,
    public dialogConfig: DialogConfig,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {    
  }

  ngOnInit(): void {
    this.initializeComponent(this.config);
  }

  private initializeComponent(config: any): void {
    if (config) {
      console.log('Initializing with config:', config);
      this.results = config.results || {};
      this.isLoaded = config.loaded;
      this.renderedContent = this.formatResultsForHTML(this.results);
      this.cdr.markForCheck();
    }
  }

  private sanitizeBase64(str: string): string {
    return str.replace(/[^A-Za-z0-9+/=]/g, '');
  }

  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  private formatResultsForHTML(results: any): SafeHtml {
    let html = "";

    if (results.insights?.length) {
      html += "<h3>Insights</h3><ul>";
      results.insights.forEach((insight: string) => {
        html += `<li>${this.escapeHtml(insight)}</li>`;
      });
      html += "</ul>";
    }

    if (results.visualizations?.length) {
      html += "<h3>Visualizations</h3>";
      results.visualizations.forEach((viz: any) => {
        const safeImg = this.sanitizeBase64(viz.data);
        html += `
          <div class='chart'>
            <h4>${this.escapeHtml(viz.title)}</h4>
            <img src="data:image/png;base64,${safeImg}" 
                 alt="${this.escapeHtml(viz.title)}"
                 style="width:100%; max-width:800px; border:1px solid #ccc; margin-bottom:1em;" />
          </div>`;
      });
    }

    if (results.tables?.length) {
      html += "<h3>Tables</h3>";
      results.tables.forEach((tbl: any) => {
        html += `
          <div class='table-block'>
            <h4>${this.escapeHtml(tbl.title)}</h4>
            <pre style="overflow-x:auto; background:#f9f9f9; padding:1em; border:1px solid #ddd;">
${this.escapeHtml(tbl.markdown)}</pre>
          </div>`;
      });
    }

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Changes detected:', changes);
    if (changes.config) {
      const newConfig = changes.config.currentValue;
      if (newConfig) {
        console.log('New config received:', newConfig);
        this.initializeComponent(newConfig);
      }
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }
}